import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

const EditRecordsPage = () => {
  const navigate = useNavigate();
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasSearched, setHasSearched] = useState(false);
  const recordsPerPage = 10;

  // Search form state
  const [searchParams, setSearchParams] = useState({
    baby_date_of_birth: '',
    mother_name: '',
    registration_id: ''
  });

  // Reference data for display
  const [referenceData, setReferenceData] = useState({
    genders: [],
    ethnicities: [],
    religions: [],
    birthPlaces: [],
    deliveryTypes: [],
    positions: [],
    districts: [],
    townships: [],
    clinics: [],
    villages: []
  });

  // Load reference data on component mount
  const loadReferenceData = useCallback(async () => {
    try {
      const [
        genders,
        ethnicities,
        religions,
        birthPlaces,
        deliveryTypes,
        positions,
        districts
      ] = await Promise.all([
        api.referenceData.getGender().catch(() => []),
        api.referenceData.getEthnicities().catch(() => []),
        api.referenceData.getReligions().catch(() => []),
        api.referenceData.getBirthPlaces().catch(() => []),
        api.referenceData.getDeliveryTypes().catch(() => []),
        api.referenceData.getPositions().catch(() => []),
        api.referenceData.getDistricts().catch(() => [])
      ]);

      setReferenceData({
        genders: genders || [],
        ethnicities: ethnicities || [],
        religions: religions || [],
        birthPlaces: birthPlaces || [],
        deliveryTypes: deliveryTypes || [],
        positions: positions || [],
        districts: districts || [],
        townships: [],
        clinics: []
      });
    } catch (error) {
      console.error('❌ Error loading reference data:', error);
    }
  }, []);

  useEffect(() => {
    loadReferenceData();
  }, [loadReferenceData]);

  // Helper function to get reference data name by ID
  const getReferenceDataName = (dataArray, id) => {
    if (!id || !dataArray) return '';
    const item = dataArray.find(item => item.id === id);
    return item?.name || item?.district_name || item?.township_name || item?.clinic_name || '';
  };

  // Get birth place name
  const getBirthPlaceName = (id) => {
    return getReferenceDataName(referenceData.birthPlaces, id);
  };

  // Handle search form input changes
  const handleSearchInputChange = (e) => {
    const { name, value } = e.target;
    setSearchParams(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Perform search
  const handleSearch = async (e) => {
    e.preventDefault();
    
    // Validate search parameters
    if (!searchParams.baby_date_of_birth && !searchParams.mother_name && !searchParams.registration_id) {
      setError('Please enter at least one search criteria (Birth Date, Mother Name, or Registration ID)');
      return;
    }

    setSearchLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      // Build search parameters - only add non-empty values
      const searchQuery = {};
      if (searchParams.baby_date_of_birth) {
        // For birth date search, use start_date and end_date for exact date match
        searchQuery.start_date = searchParams.baby_date_of_birth;
        searchQuery.end_date = searchParams.baby_date_of_birth;
      }
      if (searchParams.mother_name && searchParams.mother_name.trim()) {
        searchQuery.mother_name = searchParams.mother_name.trim();
      }
      if (searchParams.registration_id && searchParams.registration_id.trim()) {
        searchQuery.registration_id = searchParams.registration_id.trim();
      }

      // Add pagination parameters
      searchQuery.page = 1;
      searchQuery.limit = recordsPerPage;

      const response = await api.birthRecords.getBirthRecords(searchQuery);

      // Handle API response structure (same as SearchForm)
      const records = response?.data || [];
      const total = response?.pagination?.total || records.length || 0;
      
      setSearchResults(records);
      setTotalRecords(total);
      setCurrentPage(1);
      
      // Show a message if no records found after search
      if (records.length === 0) {
        setError('No records found matching your search criteria');
      }
    } catch (error) {
      console.error('❌ Search failed:', error);
      setError(`Search failed: ${error.message}`);
    } finally {
      setSearchLoading(false);
    }
  };

  // Load all records
  const handleLoadAllRecords = async () => {
    setSearchLoading(true);
    setError(null);
    
    try {
      const result = await api.birthRecords.getBirthRecords({
        page: currentPage,
        limit: recordsPerPage
      });
      
      setSearchResults(result.data || []);
      setTotalRecords(result.pagination?.total || 0);
      setHasSearched(true);
      
    } catch (error) {
      setError(`Failed to load records: ${error.message}`);
    } finally {
      setSearchLoading(false);
    }
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchParams({
      baby_date_of_birth: '',
      mother_name: '',
      registration_id: ''
    });
    setSearchResults([]);
    setTotalRecords(0);
    setCurrentPage(1);
    setHasSearched(false);
    setError(null);
  };

  // Handle edit record
  const handleEdit = (recordId) => {
    navigate(`/edit-record/${recordId}`);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  // Pagination
  const totalPages = Math.ceil(totalRecords / recordsPerPage);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
            <h1 className="text-2xl font-bold text-white">Edit Birth Records</h1>
            <p className="text-blue-100 mt-1">Search and edit existing birth records</p>
          </div>

          {/* Search Form */}
          <div className="p-6 border-b border-gray-200">
            <form onSubmit={handleSearch} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Registration ID
                  </label>
                  <input
                    type="text"
                    name="registration_id"
                    value={searchParams.registration_id}
                    onChange={handleSearchInputChange}
                    placeholder="Enter registration ID"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Birth Date
                  </label>
                  <input
                    type="date"
                    name="baby_date_of_birth"
                    value={searchParams.baby_date_of_birth}
                    onChange={handleSearchInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mother Name
                  </label>
                  <input
                    type="text"
                    name="mother_name"
                    value={searchParams.mother_name}
                    onChange={handleSearchInputChange}
                    placeholder="Enter mother's name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  type="submit"
                  disabled={searchLoading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center"
                >
                  {searchLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Searching...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      Search Records
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleLoadAllRecords}
                  disabled={searchLoading}
                  className="px-6 py-2 bg-green-600 text-white rounded-md font-medium hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                  Load All Records
                </button>

                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="px-6 py-2 bg-gray-600 text-white rounded-md font-medium hover:bg-gray-700 flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Clear
                </button>
              </div>
            </form>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-50 border-l-4 border-red-400">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Search Results */}
          {searchResults.length > 0 && totalRecords > 0 && (
            <div className="p-6">
              <div className="mb-4 flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-800">
                  Search Results ({totalRecords} records found)
                </h2>
              </div>

              {/* Enhanced Table with Father and Birth Place Details */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 border border-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">
                        Registration ID
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">
                        Baby's Name
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">
                        Father Name
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">
                        Mother Name
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {searchResults.map((record) => (
                      <tr key={record.id} className="hover:bg-gray-50">
                        <td className="px-3 py-3 text-sm text-gray-900 border-r border-gray-300 font-medium">
                          <div className="font-semibold text-blue-600">{record.registration_id}</div>
                        </td>
                        <td className="px-3 py-3 text-sm text-gray-900 border-r border-gray-300">
                          <div className="font-semibold">{record.baby_name}</div>
                        </td>
                        <td className="px-3 py-3 text-sm text-gray-900 border-r border-gray-300">
                          <div className="font-medium text-gray-800">
                            {record.father_name || <span className="text-gray-400 italic">Not provided</span>}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-sm text-gray-900 border-r border-gray-300">
                          <div className="font-medium">{record.mother_name}</div>
                          {record.mother_national_id && (
                            <div className="text-xs text-gray-600">{record.mother_national_id}</div>
                          )}
                        </td>
                        <td className="px-3 py-3 text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEdit(record.id)}
                              className="text-blue-600 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-md transition-colors duration-200 flex items-center"
                              title="Edit Record"
                            >
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                              Edit
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-6 flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing page {currentPage} of {totalPages} ({totalRecords} total records)
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-2 text-sm text-gray-700">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* No Results */}
          {(searchResults.length === 0 || totalRecords === 0) && !searchLoading && !error && hasSearched && (
            <div className="p-8 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No records found</h3>
              <p className="mt-1 text-sm text-gray-500">
                No birth records match your search criteria. Try adjusting your search parameters.
              </p>
            </div>
          )}

          {/* Instructions */}
          {!hasSearched && !searchLoading && !error && (
            <div className="p-8 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">Search for Birth Records</h3>
              <p className="mt-1 text-sm text-gray-500">
                Enter a birth date or mother's name to find records that you can edit.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditRecordsPage;
